import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { refreshToken as fetchNewToken } from '../lib/apiClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const refreshTimeout = useRef(null);

  // Sets a timeout to refresh the access token before it expires
  const setAuthTimeout = useCallback((expirationTime) => {
    const currentTime = Date.now();
    const delay = expirationTime - currentTime - 30 * 1000; // Refresh 30s before expiry

    if (delay > 0) {
      refreshTimeout.current = setTimeout(async () => {
        try {
          const data = await fetchNewToken();
          const token = data.newAccessToken || data.token;
          const refreshToken = data.newRefreshToken || data.refreshToken;

          // Decode user info from token
          const newDecodedToken = jwtDecode(token);

          // Store all fields in localStorage
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('username', newDecodedToken.username || newDecodedToken.name || '');
          localStorage.setItem('role', newDecodedToken.role || '');
          localStorage.setItem('userId', newDecodedToken.userId || '');

          setIsAuthenticated(true);
          setUser({
            username: newDecodedToken.username || newDecodedToken.name || '',
            role: newDecodedToken.role || '',
            userId: newDecodedToken.userId || '',
          });
          setAuthTimeout(newDecodedToken.exp * 1000);
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('role');
          localStorage.removeItem('username');
          localStorage.removeItem('userId');
          setIsAuthenticated(false);
          setUser(null);
        }
      }, delay);
    }
  }, []);

  // Checks token expiration and sets up auto refresh
  const checkTokenExpiration = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const isExpired = decodedToken.exp * 1000 < Date.now();

        if (isExpired) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('role');
          localStorage.removeItem('username');
          localStorage.removeItem('userId');
          setIsAuthenticated(false);
          setUser(null);
        } else {
          // Always update localStorage fields in case backend changed anything
          localStorage.setItem('username', decodedToken.username || decodedToken.name || '');
          localStorage.setItem('role', decodedToken.role || '');
          localStorage.setItem('userId', decodedToken.userId || '');
          setIsAuthenticated(true);
          setUser({
            username: decodedToken.username || decodedToken.name || '',
            role: decodedToken.role || '',
            userId: decodedToken.userId || '',
          });
          setAuthTimeout(decodedToken.exp * 1000);
        }
      } catch (e) {
        // If decode fails (token is corrupted), force logout
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('role');
        localStorage.removeItem('username');
        localStorage.removeItem('userId');
        setIsAuthenticated(false);
        setUser(null);
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [setAuthTimeout]);

  useEffect(() => {
    checkTokenExpiration();
    return () => {
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
    };
  }, [checkTokenExpiration]);

  // LOGIN: accept tokens + user data, always save refreshToken if present!
  const login = (token, user) => {
    localStorage.setItem('token', token);
    if (user.refreshToken) localStorage.setItem('refreshToken', user.refreshToken);
    localStorage.setItem('role', user.role || '');
    localStorage.setItem('username', user.username || '');
    localStorage.setItem('userId', user.userId || '');
    setIsAuthenticated(true);
    setUser(user);
    const decodedToken = jwtDecode(token);
    setAuthTimeout(decodedToken.exp * 1000);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    localStorage.removeItem('selectedBusiness');
    localStorage.removeItem('selectedBusinessId');
    localStorage.removeItem('userId');
    setIsAuthenticated(false);
    setUser(null);
    if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);